import { useEffect, useState } from "react";
import { Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useNastaveniaGlobal,
  useUpdateNastaveniaGlobal,
} from "@/api/hooks/useNastaveniaGlobal";

/**
 * Ktoré hrúbky sú pracovná doska.
 *
 * Pracovná doska sa kupuje hotová na bežné metre (tyč 4100 mm), nie ako
 * tabuľa — import ju preto pýta z PD cenníka a nie spomedzi plošných
 * dosiek, a do porezu tabúľ nejde.
 *
 * Nastavenie, nie konštanta v kóde: keď pribudne iná hrúbka PD, nemá to
 * byť ďalšie kolo vývoja.
 */
export function PracovneDoskySection(): JSX.Element {
  const query = useNastaveniaGlobal();
  const update = useUpdateNastaveniaGlobal();
  const [text, setText] = useState("");

  const ulozene = query.data?.data?.pracovna_doska_hrubky;
  const predvolene = query.data?.data?.pracovna_doska_hrubky_default ?? [38];
  useEffect(() => {
    setText((ulozene ?? predvolene).join(", "));
    // `predvolene` je konštanta zo servera — mení sa len s odpoveďou
  }, [ulozene, predvolene.join(",")]);

  async function uloz(): Promise<void> {
    const hrubky = text
      .split(",")
      .map((h) => h.trim())
      .filter(Boolean)
      .map(Number);
    if (hrubky.some((h) => !Number.isInteger(h) || h <= 0)) {
      toast({
        variant: "destructive",
        title: "Neuložené",
        description: "Hrúbky zadaj ako celé čísla oddelené čiarkou (38).",
      });
      return;
    }
    try {
      await update.mutateAsync({ pracovna_doska_hrubky: hrubky });
      toast({ title: "Hrúbky pracovných dosiek uložené" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Neuložené",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl">
          <Ruler className="h-5 w-5 text-primary" />
          Pracovné dosky
        </h2>
        <p className="text-xs text-muted-foreground">
          Hrúbky, ktoré sú pracovná doska. Pri importe z TopSolidu sa
          materiál pre ne hľadá v PD cenníku (tyče na bežné metre), nie
          medzi plošnými doskami — a nerežú sa z tabúľ, idú do plánu
          krátenia dĺžkových.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label htmlFor="pd-hrubky">Hrúbky (mm), oddelené čiarkou</Label>
          <Input
            id="pd-hrubky"
            className="h-9 w-48"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={predvolene.join(", ")}
          />
        </div>
        <Button size="sm" onClick={() => void uloz()}>
          Uložiť
        </Button>
        {ulozene == null ? (
          <span className="text-xs text-muted-foreground">
            Platí predvolené {predvolene.join(", ")} mm.
          </span>
        ) : null}
      </div>
    </section>
  );
}
